/**
 * Map Details (standalone) — the map gallery's explorer, reachable with no game
 * in scope. Reads the map purely from `/api/v1/maps/:id`.
 *
 * Thin wrapper around the shared MapExplorerView (same component the in-game
 * map-details screen uses — see components/map/MapExplorerView), so the two
 * never drift apart.
 */

import React from 'react'
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { MapExplorerView } from '../../../components/map/MapExplorerView'
import { useMap } from '../../../hooks/useMaps'
import { goBackOrHome } from '../../../lib/goBack'

export default function StandaloneMapDetailsScreen() {
  const { id } = useLocalSearchParams<{ id: string }>()
  const router = useRouter()
  const { data: map, isLoading, error } = useMap(id)

  if (isLoading) {
    return (
      <SafeAreaView className="flex-1 bg-canvas items-center justify-center">
        <ActivityIndicator color="#8aa14a" />
        <Text className="font-typed text-ink-soft mt-2">Loading map…</Text>
      </SafeAreaView>
    )
  }
  // Show the map whenever we HAVE one, even if the background refetch failed —
  // `placeholderData` means `map` may be the bundled copy while the network is
  // down, and "Map not found" over perfectly good geometry is a worse answer
  // than slightly stale geometry.
  if (!map) {
    return (
      <SafeAreaView className="flex-1 bg-canvas items-center justify-center gap-3 px-6">
        <Text className="font-stencil text-ink uppercase">Map not found</Text>
        <TouchableOpacity
          onPress={() => goBackOrHome(router)}
          accessibilityLabel="Go back"
          accessibilityRole="button"
          className="bg-paper-up border border-olive rounded-md px-4 py-2"
        >
          <View>
            <Text className="font-stencil text-ink">← Back</Text>
          </View>
        </TouchableOpacity>
      </SafeAreaView>
    )
  }

  // Standalone gallery explorer: no game to view, so hide the Game|Regions
  // toggle (always regions).
  return <MapExplorerView map={map} onBack={() => goBackOrHome(router)} showModeToggle={false} />
}
